/**
 * Kuantiti dalam unit mili (1/1000 unit).
 *
 * Peraturan (ADR-0005):
 * - Kuantiti disimpan sebagai integer mili-unit. `0.753 kg` → `753`.
 * - Tiada `float` untuk kuantiti. Tiga tempat perpuluhan cukup untuk gram.
 * - Harga ialah sen **per satu unit**, jadi 753 mili-unit × 4500 sen/kg = RM33.89.
 */
import { divRoundHalfUp } from './money.js';
export const QTY_SCALE = 1000;
export const UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'm'];
/** Bilangan tempat perpuluhan untuk paparan, mengikut unit. */
const UNIT_DECIMALS = {
    pcs: 0,
    g: 0,
    ml: 0,
    kg: 3,
    l: 3,
    m: 3,
};
const MAX_MAGNITUDE = 1_000_000_000_000; // 1e9 unit
export function isUnit(value) {
    return UNITS.includes(value);
}
export function assertMilliQty(value, label = 'kuantiti') {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
        throw new RangeError(`${label} mesti integer mili-unit, diterima ${value}`);
    }
    if (!Number.isSafeInteger(value) || Math.abs(value) > MAX_MAGNITUDE) {
        throw new RangeError(`${label} melebihi had: ${value}`);
    }
}
/**
 * Hurai teks kuantiti kepada mili-unit.
 *
 * Diterima: `"0.753"`, `"12"`, `"1.5"`, `"-2.25"`, `"0.753 "`.
 * Ditolak: lebih daripada 3 tempat perpuluhan, aksara lain, kosong.
 */
export function parseQty(input) {
    if (typeof input !== 'string') {
        throw new TypeError('kuantiti mesti teks');
    }
    const cleaned = input.trim().replace(/\s+/g, '');
    if (cleaned === '') {
        throw new RangeError('kuantiti kosong');
    }
    const match = /^(-)?(\d*)(?:\.(\d{0,3}))?$/.exec(cleaned);
    const wholeRaw = match?.[2] ?? '';
    const fracRaw = match?.[3] ?? '';
    if (match === null || (wholeRaw === '' && fracRaw === '')) {
        throw new RangeError(`bukan kuantiti yang sah: "${input}"`);
    }
    const whole = wholeRaw === '' ? 0 : Number(wholeRaw);
    const frac = fracRaw === '' ? 0 : Number(fracRaw.padEnd(3, '0'));
    const milli = whole * QTY_SCALE + frac;
    assertMilliQty(milli, 'kuantiti');
    return match[1] === '-' ? -milli : milli;
}
/** Format mili-unit kepada teks, dengan bilangan perpuluhan mengikut unit. */
export function formatQty(milli, unit) {
    assertMilliQty(milli);
    const decimals = UNIT_DECIMALS[unit];
    const negative = milli < 0;
    const magnitude = Math.abs(milli);
    const whole = Math.floor(magnitude / QTY_SCALE);
    const frac = magnitude - whole * QTY_SCALE;
    const base = `${negative ? '-' : ''}${whole}`;
    if (decimals === 0) {
        // Bundarkan separuh ke atas: 1250 mili-unit → "1"
        const rounded = divRoundHalfUp(frac, QTY_SCALE);
        if (rounded === 1) {
            return `${negative ? '-' : ''}${whole + 1}`;
        }
        return base;
    }
    const fracText = String(frac).padStart(3, '0').slice(0, decimals);
    return `${base}.${fracText}`;
}
/**
 * Gram (seperti dibaca dari penimbang) kepada mili-unit bagi unit `kg`.
 *
 * 1 gram = 0.001 kg = 1 mili-unit. Penukaran ini wujud supaya kasir tidak perlu
 * menukar gram ke kilogram secara mental (lihat architecture.md §5.2).
 */
export function milliFromGrams(grams) {
    if (!Number.isFinite(grams)) {
        throw new RangeError(`gram mesti nombor, diterima ${grams}`);
    }
    return Math.round(grams);
}
/** Mili-unit bagi unit `kg` kepada gram. */
export function gramsFromMilli(milli) {
    assertMilliQty(milli);
    return milli;
}
/**
 * Kuantiti yang dibeli apabila pelanggan menyebut jumlah ringgit
 * ("bagi RM30") — bukan berat.
 *
 * `qty = amount / unitPrice`, dibundar kepada mili-unit terdekat.
 */
export function qtyForAmount(amountSen, unitPriceSen) {
    if (unitPriceSen <= 0) {
        throw new RangeError('harga unit mesti lebih daripada sifar untuk kira kuantiti daripada jumlah');
    }
    const milli = divRoundHalfUp(amountSen * QTY_SCALE, unitPriceSen);
    assertMilliQty(milli);
    return milli;
}
/** Gandakan kuantiti dengan harga unit, dibundar kepada sen terdekat. */
export function mulQtyPrice(qtyMilli, unitPriceSen) {
    assertMilliQty(qtyMilli);
    if (!Number.isSafeInteger(unitPriceSen)) {
        throw new RangeError(`harga unit mesti integer sen, diterima ${unitPriceSen}`);
    }
    return divRoundHalfUp(qtyMilli * unitPriceSen, QTY_SCALE);
}
//# sourceMappingURL=quantity.js.map